import React, { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import Alert from "../../../Common/Alert";
import Http from "../../../Common/Http";
import { LoaderComponent } from "../../../Common/LoaderComponent";
import IsLogin from "../../../Common/IsLogin";
import useAlertFromLocation from "../../../../hooks/useAlertFromLocation";
import useDeleteData from "../../../../hooks/useDeleteData";
import ReactDataTable from "../../../Common/ReactDataTable";

const List = () => {
  const [receiveData, setReceiveData] = useState([]);
  const [flattenedData, setFlattenedData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [debitTotal, setDebitTotal] = useState(0);
  const [creditTotal, setCreditTotal] = useState(0);
  // Get current date and first day of current month
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  
  const [fromDate, setFromDate] = useState(firstDayOfMonth.toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(today.toISOString().split('T')[0]);

  const fetchData = async (from = '', to = '') => {
    setLoading(true);
    try {
      let url = "/journal-voucher";
      const params = new URLSearchParams();
      if (from) params.append('from_date', from);
      if (to) params.append('to_date', to);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await Http.get(url);
      setReceiveData(res.data.list);
      setDebitTotal(res.data.debit);
      setCreditTotal(res.data.credit);
      
      // Flatten the nested data structure for ReactDataTable
      const flattened = res.data.list
        .map(({ items }) => Object.entries(items))
        .flat()
        .map(([key, value], index) => ({
          id: value.id,
          journal_voucher_id: value.journal_voucher.id,
          created_by: key == 0 ? value.created_by?.name : "",
          date: key == 0 ? value.journal_voucher.date : "",
          serial_number: key == 0 ? value.journal_voucher.serial_number : "",
          type: key == 0 ? value.journal_voucher.type : "",
          chart_of_account: value.chart_of_account?.code + " - " + value.chart_of_account?.name,
          sub_category: value.sub_category != null
            ? value.sub_category?.name == "new"
              ? value.sub_category.new_name
              : value.sub_category?.name == "hr"
              ? value.hr.card_no + " - " + value.hr.name
              : value.sub_category?.name == "assets"
              ? value.asset.code + " - " + value.asset.name
              : value.sub_category?.name == "supplier"
              ? value.supplier.code + " - " + value.supplier?.name
              : value.sub_category?.name == "distributor"
              ? value.distributor.code + " - " + value.distributor?.name
              : value.sub_category?.name != "hr" && value.sub_category?.name != "new"
              ? value.sub_category?.name
              : null
            : null,
          bill_number: value.bill_number,
          bill_date: value.bill_date,
          debit: value.debit,
          credit: value.credit,
          isFirstRow: key == 0
        }));
      setFlattenedData(flattened);
    } catch (error) {
      console.error("Fetch error:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData(fromDate, toDate);
  }, []);

  // Handle date filtering
  const handleDateFilter = () => {
    fetchData(fromDate, toDate);
  };

  const handleClearFilter = () => {
    const today = new Date();
    const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
    const defaultFromDate = firstDayOfMonth.toISOString().split('T')[0];
    const defaultToDate = today.toISOString().split('T')[0];
    
    setFromDate(defaultFromDate);
    setToDate(defaultToDate);
    fetchData(defaultFromDate, defaultToDate);
  };

  // to delete data call
  const deleteData = useDeleteData(() => fetchData(fromDate, toDate));
  // to show alert message
  const alert = useAlertFromLocation();

  // columns for the datatable
  const cols = [
    { field: "created_by", header: "Created By" },
    { field: "date", header: "Date" },
    { field: "serial_number", header: "JV No" },
    { field: "type", header: "Type" },
    { field: "chart_of_account", header: "Chart Of Accounts" },
    { field: "sub_category", header: "Sub Category" },
    { field: "bill_number", header: "Bill No" },
    { field: "bill_date", header: "Bill Date" },
    { field: "debit", header: "Debit" },
    { field: "credit", header: "Credit" },
  ];

  // action buttons for the datatable
  const actionTemplate = (rowData, deleteData) => {
    if (!rowData.isFirstRow) return null;
    
    return (
      <div className="text-xs text-nowrap">
        <Link
          to={{
            pathname: "/edit-journal-voucher/" + rowData.journal_voucher_id,
          }}
          className="btn btn-primary btn-sm"
          title="Edit"
        >
          <i className="fa fa-pencil" aria-hidden="true"></i>
        </Link>
        <Link
          to={{
            pathname: "/journal-voucher-details/" + rowData.journal_voucher_id,
          }}
          className="btn btn-info btn-sm ms-1"
          title="Detail"
        >
          <i className="fa fa-list" aria-hidden="true"></i>
        </Link>
        <button 
          className="btn btn-danger btn-sm ms-1" 
          onClick={() => deleteData(rowData.journal_voucher_id, "journal-voucher", "Journal Voucher")} 
          title="Delete"
        >
          <i className="fa fa-trash"></i>
        </button>
      </div>
    );
  };

  return (
    <div>
      <IsLogin></IsLogin>
      <style>
        {`
          .journal-voucher-header {
            background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
            color: white;
            padding: 25px;
            border-radius: 12px;
            margin-bottom: 25px;
            box-shadow: 0 8px 25px rgba(102, 126, 234, 0.15);
          }
          .stats-card {
              border-radius: 15px;
              padding: 25px 20px;
              color: white;
              position: relative;
              overflow: hidden;
              transition: all 0.3s ease;
              box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
            }
            .stats-card:hover {
              transform: translateY(-5px);
              box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
            }
            .stats-card-blue {
              background: linear-gradient(135deg, #1e88e5, #1976d2);
            }
            .stats-card-green {
              background: linear-gradient(135deg, #43a047, #388e3c);
            }
            .stats-card-orange {
              background: linear-gradient(135deg, #fb8c00, #f57c00);
            }
            .stats-card-teal {
              background: linear-gradient(135deg, #00acc1, #0097a7);
            }
            .stats-icon {
              position: absolute;
              right: 20px;
              top: 50%;
              transform: translateY(-50%);
              font-size: 2.5rem;
              opacity: 0.3;
            }
            .stats-content {
              position: relative;
              z-index: 2;
            }
            .stats-value {
              font-size: 1.8rem;
              font-weight: 700;
              color: white;
              margin-bottom: 5px;
              line-height: 1.2;
            }
            .stats-label {
              color: rgba(255, 255, 255, 0.9);
              font-size: 0.9rem;
              font-weight: 500;
              margin-bottom: 0;
            }
            .stats-count {
              font-size: 2.5rem;
              font-weight: 800;
              color: white;
              margin-bottom: 5px;
              line-height: 1;
            }
           .stats-section {
             margin-bottom: 35px;
           }
           .stats-section-title {
             text-align: center;
             margin-bottom: 25px;
             color: #2c3e50;
             font-weight: 700;
             font-size: 1.3rem;
             position: relative;
           }
           .stats-section-title::after {
             content: '';
             position: absolute;
             bottom: -8px;
             left: 50%;
             transform: translateX(-50%);
             width: 60px;
             height: 3px;
             background: linear-gradient(90deg, #667eea, #764ba2);
             border-radius: 2px;
           }
          .main-card {
            background: white;
            border-radius: 15px;
            box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
            border: 1px solid #e9ecef;
            overflow: hidden;
          }
          .card-header-custom {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            padding: 20px 25px;
            border-bottom: 2px solid #dee2e6;
          }
          .card-body-custom {
            padding: 25px;
          }
          .page-title {
            font-size: 1.75rem;
            font-weight: 700;
            color: white;
            margin-bottom: 8px;
          }
          .page-subtitle {
            color: rgba(255, 255, 255, 0.8);
            font-size: 1rem;
            margin-bottom: 0;
          }
          .btn-new {
            background: linear-gradient(135deg, #28a745, #20c997);
            border: none;
            border-radius: 8px;
            padding: 10px 20px;
            font-weight: 600;
            color: white;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(40, 167, 69, 0.3);
          }
          .btn-new:hover {
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(40, 167, 69, 0.4);
            color: white;
          }
          .table-header {
            background: #f8f9fa;
            padding: 15px 20px;
            border-bottom: 2px solid #dee2e6;
            font-weight: 600;
            color: #495057;
          }
          .filter-section .card {
            border: 1px solid #e3e6f0;
            border-radius: 10px;
            box-shadow: 0 2px 10px rgba(0, 0, 0, 0.05);
          }
          .filter-section .card-header {
            background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
            border-bottom: 1px solid #dee2e6;
            border-radius: 10px 10px 0 0;
            padding: 12px 20px;
          }
          .filter-section .card-header h6 {
            color: #495057;
            font-weight: 600;
          }
          .filter-section .form-label {
            font-weight: 500;
            color: #495057;
            margin-bottom: 5px;
          }
          .filter-section .form-control {
            border-radius: 6px;
            border: 1px solid #d1d3e2;
            transition: all 0.3s ease;
          }
          .filter-section .form-control:focus {
            border-color: #667eea;
            box-shadow: 0 0 0 0.2rem rgba(102, 126, 234, 0.25);
          }
          .filter-section .btn {
            border-radius: 6px;
            font-weight: 500;
            transition: all 0.3s ease;
          }
          .filter-section .badge {
            font-size: 0.75rem;
            padding: 6px 12px;
            border-radius: 20px;
          }
        `}
      </style>
      <div>
        <div className="am-mainpanel">
          <div className="am-pagetitle">
            <div>
              <h5 className="am-title mb-1">Journal Voucher Management</h5>
              <p className="text-muted mb-0">Track and manage petty cash counts with detailed denomination breakdown</p>
            </div>
            <div className="d-flex gap-2">
              <Link to="/create-journal-voucher" className="btn btn-primary">
                <i className="fa fa-plus-square mg-r-6"></i>Create New Voucher
              </Link>
              <button 
                className="btn btn-outline-secondary"
                onClick={() => window.location.reload()}
                title="Refresh Data"
              >
                <i className="fa fa-refresh"></i>
              </button>
            </div>
          </div>
          <div className="am-pagebody">
            <Alert alert={alert} />
            
            {/* Date Filter Section */}
            <div className="filter-section mb-4">
              <div className="card">
                <div className="card-header">
                  <h6 className="mb-0">
                    <i className="fa fa-filter me-2"></i>Filter by Date Range
                  </h6>
                </div>
                <div className="card-body">
                  <div className="row align-items-end">
                    <div className="col-md-3">
                      <label className="form-label">From Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={fromDate}
                        onChange={(e) => setFromDate(e.target.value)}
                        placeholder="Select from date"
                      />
                    </div>
                    <div className="col-md-3">
                      <label className="form-label">To Date</label>
                      <input
                        type="date"
                        className="form-control"
                        value={toDate}
                        onChange={(e) => setToDate(e.target.value)}
                        placeholder="Select to date"
                      />
                    </div>
                    <div className="col-md-6">
                      <div className="d-flex gap-2">
                        <button
                          className="btn btn-primary"
                          onClick={handleDateFilter}
                        >
                          <i className="fa fa-search me-1"></i>Search
                        </button>
                        <button
                          className="btn btn-outline-secondary"
                          onClick={handleClearFilter}
                          title="Reset to current month"
                        >
                          <i className="fa fa-refresh me-1"></i>Reset
                        </button>
                        <div className="d-flex align-items-center ms-3">
                          <span className="badge bg-info">
                            <i className="fa fa-calendar me-1"></i>
                            {fromDate && toDate 
                              ? `${fromDate} to ${toDate}`
                              : fromDate 
                              ? `From: ${fromDate}`
                              : `To: ${toDate}`
                            }
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Enhanced Statistics Section */}
             <div className="stats-section">
               {/* <h4 className="stats-section-title">
                 <i className="fa fa-chart-bar me-2"></i>Financial Overview
               </h4> */}
               <div className="row">
                 <div className="col-lg-3 col-md-6 ">
                   <div className="stats-card stats-card-blue">
                     <div className="stats-content">
                       <div className="stats-label">Total Records</div>
                       <div className="stats-count">{flattenedData.length}</div>
                     </div>
                     <div className="stats-icon">
                       <i className="fa fa-list"></i>
                     </div>
                   </div>
                 </div>
                 <div className="col-lg-3 col-md-6 ">
                   <div className="stats-card stats-card-green">
                     <div className="stats-content">
                       <div className="stats-label">Total Debit</div>
                       <div className="stats-value">৳{debitTotal.toLocaleString()}</div>
                     </div>
                     <div className="stats-icon">
                       <i className="fa fa-arrow-up"></i>
                     </div>
                   </div>
                 </div>
                 <div className="col-lg-3 col-md-6 ">
                   <div className="stats-card stats-card-orange">
                     <div className="stats-content">
                       <div className="stats-label">Total Credit</div>
                       <div className="stats-value">৳{creditTotal.toLocaleString()}</div>
                     </div>
                     <div className="stats-icon">
                       <i className="fa fa-arrow-down"></i>
                     </div>
                   </div>
                 </div>
                 <div className="col-lg-3 col-md-6 ">
                   <div className="stats-card stats-card-teal">
                     <div className="stats-content">
                       <div className="stats-label">Balance Difference</div>
                       <div className="stats-value">৳{Math.abs(debitTotal - creditTotal).toLocaleString()}</div>
                     </div>
                     <div className="stats-icon">
                       <i className="fa fa-balance-scale"></i>
                     </div>
                   </div>
                 </div>
               </div>
             </div>

            {/* Main Data Table */}
            <div className="main-card">
              <div className="card-header-custom">
                <h5 className="mb-0" style={{color: '#495057', fontWeight: '600'}}>
                  <i className="fa fa-table me-2"></i>Journal Voucher Entries
                </h5>
              </div>
              <div className="card-body-custom">
                <ReactDataTable 
                  cols={cols} 
                  receiveData={flattenedData} 
                  isLoading={loading} 
                  actionTemplate={actionTemplate} 
                  deleteData={deleteData} 
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default List;
